import {
	RichTextEditorContentHandle,
	RichTextEditorContent,
	RichTextEditorToolbar,
	createInitialEditorValue,
	DropOverActionList,
	ActionListItem,
	ActionListDivider,
	useRichTextEditorIntermediateState,
	RichTextEditorIntermediateStateHandlers,
	useRichTextEditorRewriteTools,
	RichTextEditorRewriteToolsResult,
	SelectionAnchor,
	DescendantRootsRegistration,
	DropOverContextData,
	DropOverContext
} from "client/Components";
import { useProseMirrorRichTextEditorTool } from "client/Components/RichTextEditor/tools/useProseMirrorRichTextEditorTool";
import { MailMessageLocationModel } from "client/Api/Email/common";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	AlertManager,
	DragOverEventArgs,
	DragOverEventResult,
	DropEventArgs,
	useAlerts,
	useDragAndDropEvents,
	UsePointerEventOptions,
	usePointerEventOutside
} from "client/Hooks";
import { Command } from "prosemirror-state";
import {
	CSSProperties,
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./Body.module.less";
import {
	AttachmentItem,
	AttachmentKind,
	UploadedInlineAttachment
} from "../AttachmentManager/common";
import { Attachments } from "./Attachments/Attachments";
import { Coordinate, Rect } from "client/Misc/commonTypes";
import { localize } from "client_legacy/ndaw/culture";
import {
	emailComposerImagePlugin,
	insertInlineImage,
	setAttachments,
	setDraftLocation
} from "./prosemirror/emailComposerImagePlugin";
import {
	deleteImageAt,
	insertHtml,
	insertParagraph,
	placeCaretAtTheBeginning,
	placeCaretAtTheEnd,
	setFontStyle,
	setImageScale
} from "./prosemirror/customCommands";
import classNames from "classnames";
import { FontStyleModel } from "client/Api/common";
import { partition } from "client_legacy/ndaw/arrayHelpers";
import {
	EMPTY_ARRAY,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { getHtml } from "client/Components/RichTextEditor/htmlConversion";
import { isProseMirror, ProseMirrorRichTextEditorState } from "client/Components/RichTextEditor/types";
import { FileSelectionEntry, SelectionEntry } from "client_legacy/FileUploader/utils";
import { RewriteToolTargetKind } from "client/Api/AiAssistant/aiAssistant";
import { RewriteTool } from "./RewriteTool";
import { VisibleAreaTracker } from "client/Components/VisibleAreaTracker/VisibleAreaTracker";
import { RewriteToolEditorKind, useRewriteToolsService } from "client/Features/ChatAssistant/useRewriteToolsService";
import { useIsAiAssistantAvailable } from "client/Features/ChatAssistant/useIsAiAssistantAvailable";

export type BodyProps = {
	bodyAttachable: IValueInputAttachable<string>;
	attachments: readonly AttachmentItem[];
	draftLocation: MailMessageLocationModel;
	onDeleteAttachment(uid: string): void;
	onUploadFileAttachments(...files: readonly File[]): void;
};

export type BodyHandle = {
	applyFontStyle(fontStyle: FontStyleModel): void;
	placeCaretAtTheBeginning(): void;
	placeCaretAtTheEnd(): void;
	insertEmptyParagraph(): void;
	insertSignature(signature: string): void;
	insertInlineImage(attachment: UploadedInlineAttachment): void;
	focusEditor(): void;
	getImmediateBodyHtml(): string;
};

export const Body = forwardRef(
	(props: BodyProps, ref: Ref<BodyHandle>): ReactElement => {
		const {
			onDeleteAttachment,
			onUploadFileAttachments,
			bodyAttachable,
			attachments,
			draftLocation
		} = props;

		const alertManager: AlertManager = useAlerts();
		const [imageContextMenuState, setImageContextMenuState] = useState<ImageContextMenuState>({ isOpen: false });
		const [isDropTarget, setIsDropTarget] = useState<boolean>(false);
		const [shouldPersistIntermediateState, setShouldPersistIntermediateState] = useState<boolean>(false);
		const [selectionMenuAnchor, setSelectionMenuAnchor] = useState<SelectionAnchor | null>(null);
		const [visibleContentArea, setVisibleContentArea] = useState<Rect>();
		const [rewriteMenuRootRegistrations, setRewriteMenuRootRegistrations] = useState<readonly DescendantRootsRegistration[]>(EMPTY_ARRAY);

		const isAiAssistantAvailable: boolean = useIsAiAssistantAvailable();

		const contentRef: RefObject<RichTextEditorContentHandle | null> = useRef<RichTextEditorContentHandle>(null);
		const rewriteToolContainerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

		const dragOverHandler = useCallback(
			(args: DragOverEventArgs): DragOverEventResult => {
				const { types } = args;
				if (types.indexOf("Files") === -1) {
					return { isAccepted: false };
				}

				return {
					isAccepted: true,
					dropEffect: "copy"
				};
			},
			[]
		);

		const dropHandler = useCallback(
			async (args: DropEventArgs): Promise<void> => {
				const fileEntries: readonly SelectionEntry[] = await args.getFiles();

				const [directories, files] = partition(fileEntries, f => f.isDirectory);

				if (directories.length > 0) {
					alertManager.showError(localize("_YouCantUploadFolders"));
				}

				if (files.length > 0) {
					onUploadFileAttachments(...(files as FileSelectionEntry[]).map(f => f.file));
				}
			},
			[alertManager, onUploadFileAttachments]
		);

		const dragEnterHandler = useCallback(
			(args: DragOverEventArgs): void => {
				if (args.types.indexOf("Files") !== -1) {
					setIsDropTarget(true);
				}
			},
			[]
		);

		const dragLeaveHandler = useCallback(
			(): void => {
				setIsDropTarget(false);
			},
			[]
		);

		const {
			isDropTarget: _,
			...dragAndDropEventHandlers
		} = useDragAndDropEvents({
			onDragOver: dragOverHandler,
			onDrop: dropHandler,
			onDragEnter: dragEnterHandler,
			onDragLeave: dragLeaveHandler
		});

		const imageContextMenuHandler = useCallback(
			(
				target: HTMLImageElement,
				targetNodePosition: number,
				position: Coordinate
			): void => {
				setImageContextMenuState({
					isOpen: true,
					target,
					position,
					targetNodePosition
				});
			},
			// NOTE: must not have any dependencies, otherwise editor state should be reconfigured with the new instance of the plugin
			[]
		);

		const [editorInitialValue] = useState(
			(): ProseMirrorRichTextEditorState => createInitialEditorValue([emailComposerImagePlugin(imageContextMenuHandler)])
		);

		const richTextEditorState: RichTextEditorIntermediateStateHandlers = useRichTextEditorIntermediateState(editorInitialValue);

		useEffect(
			(): void => {
				if (!shouldPersistIntermediateState) {
					return undefined;
				}

				const {
					current: content
				} = contentRef;

				if (isNotNil(content) && !content.hasFocus()) {
					persistIntermediateState(
						richTextEditorState,
						bodyAttachable
					);
				}

				setShouldPersistIntermediateState(false);
			},
			[
				bodyAttachable,
				richTextEditorState,
				shouldPersistIntermediateState
			]
		);

		const focusContentHandler = useCallback(
			(): void => {
				contentRef.current
					?.focus();
			},
			[]
		);

		const rewriteToolsResult: RichTextEditorRewriteToolsResult = useRichTextEditorRewriteTools(
			richTextEditorState.intermediateState,
			richTextEditorState.setIntermediateState,
			focusContentHandler,
			{ kind: "selection" },
			useRewriteToolsService(
				RewriteToolTargetKind.Email,
				RewriteToolEditorKind.RichText
			)
		);

		const {
			canApplyCommand,
			applyCommand,
			focus: focusInput,
			editorState
		} = useProseMirrorRichTextEditorTool({
			value: richTextEditorState.intermediateState,
			onChange: richTextEditorState.setIntermediateState,
			onFocusInput: focusContentHandler
		});

		const openSelectionMenuHandler = useCallback(
			(anchor: SelectionAnchor | null): void => {
				const contentContainer: HTMLDivElement | null = rewriteToolContainerRef.current;

				if (!anchor || !contentContainer) {
					setSelectionMenuAnchor(null);
					return;
				}

				// NOTE: The anchor is given in viewport coordinates. We translate it into the
				// content container's local space so the button can be absolutely positioned
				// inside it and scroll together with the email content.
				const {
					left: containerLeft,
					top: containerTop
				} = contentContainer.getBoundingClientRect();

				setSelectionMenuAnchor({
					x: anchor.x - containerLeft,
					bottom: anchor.bottom - containerTop,
					top: anchor.top - containerTop,
					direction: anchor.direction
				});
			},
			[]
		);

		const applyCommandHandler = useCallback(
			(command: Command): void => {
				applyCommand(command);
				setShouldPersistIntermediateState(true);
			},
			[applyCommand]
		);

		useEffect(
			(): void => {
				applyCommandHandler(
					setAttachments(attachments)
				);
			},
			[attachments, applyCommandHandler]
		);

		useEffect(
			(): void => {
				applyCommandHandler(
					setDraftLocation(draftLocation)
				);
			},
			[draftLocation, applyCommandHandler]
		);

		useImperativeHandle(
			ref,
			(): BodyHandle => ({
				insertEmptyParagraph(): void {
					applyCommandHandler(
						insertParagraph
					);
				},
				insertSignature(signature: string): void {
					applyCommandHandler(
						insertHtml(signature)
					);
				},
				insertInlineImage(uploadedAttachment: UploadedInlineAttachment): void {
					applyCommandHandler(
						insertInlineImage(uploadedAttachment.contentId)
					);
				},
				placeCaretAtTheBeginning(): void {
					applyCommandHandler(
						placeCaretAtTheBeginning
					);
				},
				placeCaretAtTheEnd(): void {
					applyCommandHandler(
						placeCaretAtTheEnd
					);
				},
				applyFontStyle(fontStyle: FontStyleModel): void {
					applyCommandHandler(
						setFontStyle(fontStyle)
					);
				},
				focusEditor(): void {
					focusInput();
				},
				getImmediateBodyHtml(): string {
					return getHtml(editorState);
				},
			}),
			[
				applyCommandHandler,
				focusInput,
				editorState
			]
		);

		const rewriteMenuRoots = useMemo(
			(): readonly HTMLElement[] => {
				const result: HTMLElement[] = [];

				for (const registration of rewriteMenuRootRegistrations) {
					result.push(
						...registration
							.roots
							.filter(isNotNil)
					);
				}

				return result;
			},
			[rewriteMenuRootRegistrations]
		);

		usePointerEventOutside(
			useCallback(
				(): void => {
					setSelectionMenuAnchor(null);
				},
				[]
			),
			rewriteToolContainerRef.current,
			useMemo(
				(): UsePointerEventOptions => ({
					ignoreWheelEvent: true,
					// NOTE: When set to true (default) the event triggers when user releases the mouse button
					// this means if user selects text and stops selection outside of the container the assistant button
					// immediately disappears.
					shouldListenForEntireClick: false,
					otherRoots: rewriteMenuRoots
				}),
				[rewriteMenuRoots]
			)
		);

		const rewriteMenuDropOverContextData: DropOverContextData = useMemo(
			(): DropOverContextData => ({
				registerDescendantsRoots: (roots) => {
					const registration: DescendantRootsRegistration = {
						roots: roots
					};
					setRewriteMenuRootRegistrations(x => [...x, registration]);

					return (): void => {
						setRewriteMenuRootRegistrations(
							s => s.filter(x => x !== registration)
						);
					};
				}
			}),
			[]
		);

		const regularAttachments = useMemo(
			(): AttachmentItem[] => {
				return attachments.filter(a => a.kind === AttachmentKind.Normal);
			},
			[attachments]
		);

		const dropTargetMessage: CSSProperties = {
			"--drop-target-message": `"${localize("_PutFilesHere")}"`
		};

		return (
			<div
				{...setTrackingId("body")}
				{...dragAndDropEventHandlers}
				className={classNames(
					styles.root,
					{[styles.root_dropTarget]: isDropTarget}
				)}
				style={dropTargetMessage}
			>
				<div className={styles.toolbarContainer}>
					<RichTextEditorToolbar
						{...bodyAttachable.attachToRichTextEditorToolbar(richTextEditorState)}
						onFocusInput={focusContentHandler}
						hasNoPadding
						isBorderless
					/>
				</div>
				{regularAttachments.length > 0 && (
					<div className={styles.attachments}>
						<Attachments
							items={regularAttachments}
							draftLocation={draftLocation}
							onDelete={onDeleteAttachment}
						/>
						<hr className={styles.attachmentsDivider} />
					</div>
				)}
				<div className={styles.contentContainer}>
					<VisibleAreaTracker
						direction="both"
						height="auto"
						width="auto"
						trackingId="content-area-tracker"
						onChange={setVisibleContentArea}
					>
						<div
							ref={rewriteToolContainerRef}
							className={styles.rewriteToolBoundingRect}
						>
							<RichTextEditorContent
								{...bodyAttachable.attachToRichTextEditorContent(richTextEditorState)}
								ref={contentRef}
								onSelectionAnchorChanged={isAiAssistantAvailable ? openSelectionMenuHandler : undefined}
								height="fill"
								isBorderless
								hasNoPadding
								elementModifiers={{
									paragraph: {
										hasNoMargin: true
									},
									image: {
										hasNoMaxWidth: true,
										verticalAlignment: "bottom"
									}
								}}
							/>
							{rewriteToolsResult.canRewrite && selectionMenuAnchor && (
								<DropOverContext value={rewriteMenuDropOverContextData}>
									<RewriteTool
										rewriteHandlers={rewriteToolsResult.handlers}
										anchor={selectionMenuAnchor}
										containmentRect={visibleContentArea}
									/>
								</DropOverContext>
							)}
						</div>
					</VisibleAreaTracker>
				</div>
				{imageContextMenuState.isOpen && (
					<ImageActionsDropOver
						target={imageContextMenuState.target}
						position={imageContextMenuState.position}
						targetNodePosition={imageContextMenuState.targetNodePosition}
						onClose={() => setImageContextMenuState({ isOpen: false })}
						applyCommand={applyCommand}
						canApplyCommand={canApplyCommand}
						focusInput={focusInput}
					/>
				)}
			</div>
		);
	}
);
Body.displayName = "Body";

export type ImageActionsDropOverProps = {
	target: HTMLImageElement;
	targetNodePosition: number;
	position: Coordinate;
	onClose(): void;
	canApplyCommand(command: Command): boolean;
	applyCommand(command: Command): void;
	focusInput(): void;
};

export const ImageActionsDropOver = (props: ImageActionsDropOverProps): ReactElement => {
	const {
		target,
		targetNodePosition,
		onClose,
		position,
		applyCommand,
		canApplyCommand,
		focusInput
	} = props;

	const createSetScaleCommand = useCallback(
		(scale: number): Command => {
			return setImageScale(target, targetNodePosition, scale);
		},
		[target, targetNodePosition]
	);

	const setSmallScale = useMemo(
		(): Command => createSetScaleCommand(0.25),
		[createSetScaleCommand]
	);

	const setMediumScale = useMemo(
		(): Command => createSetScaleCommand(0.5),
		[createSetScaleCommand]
	);

	const setOriginalScale = useMemo(
		(): Command => createSetScaleCommand(1),
		[createSetScaleCommand]
	);

	const setBigScale = useMemo(
		(): Command => createSetScaleCommand(2),
		[createSetScaleCommand]
	);

	const deleteImage = useMemo(
		(): Command => deleteImageAt(targetNodePosition),
		[targetNodePosition]
	);

	const applyCommandAndFocus = useCallback(
		(command: Command): void => {
			applyCommand(command);
			focusInput();
		},
		[applyCommand, focusInput]
	);

	return (
		<DropOverActionList
			target={target}
			targetAlignment={{
				kind: "absolute",
				coordinate: position
			}}
			onClose={onClose}
			trackingId="email-composer-image-context-menu"
			menuTitle={localize("_Image")}
		>
			{canApplyCommand(setSmallScale) && (
				<ActionListItem
					text={localize("_Small")}
					onClick={() => applyCommandAndFocus(setSmallScale)}
					trackingId="resize-small"
				/>
			)}

			{canApplyCommand(setMediumScale) && (
				<ActionListItem
					text={localize("_Medium")}
					onClick={() => applyCommandAndFocus(setMediumScale)}
					trackingId="resize-medium"
				/>
			)}

			{canApplyCommand(setOriginalScale) && (
				<ActionListItem
					text={localize("_Original")}
					onClick={() => applyCommandAndFocus(setOriginalScale)}
					trackingId="resize-original"
				/>
			)}

			{canApplyCommand(setBigScale) && (
				<ActionListItem
					text={localize("_Big")}
					onClick={() => applyCommandAndFocus(setBigScale)}
					trackingId="resize-big"
				/>
			)}
			<ActionListDivider />
			{canApplyCommand(deleteImage) && (
				<ActionListItem
					icon="trash-can"
					text={localize("_Delete")}
					onClick={() => applyCommandAndFocus(deleteImage)}
					trackingId="delete"
				/>
				)}
		</DropOverActionList>
		);
};
ImageActionsDropOver.displayName = "ImageActionsDropOver";

type ImageContextMenuState = {
	isOpen: false;
} | {
	isOpen: true;
	target: HTMLImageElement;
	targetNodePosition: number;
	position: Coordinate;
};

function persistIntermediateState(
	richTextEditorState: RichTextEditorIntermediateStateHandlers,
	attachable: IValueInputAttachable<string>
): void {
	const {
		intermediateState,
		setHtmlThatRepresentsTheState
	} = richTextEditorState;

	if (!isProseMirror(intermediateState)) {
		throw new Error("This function only works with prosemirror state");
	}

	const html: string = getHtml(intermediateState.editorState);
	attachable.reducer.setValue(html);
	setHtmlThatRepresentsTheState(html);

	attachable.validation.triggerInputFinished();
}
